import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Label, Input, Textarea, Select } from "@/components/Field";
import CTA from "@/components/CTA";

const ROLE_OPTIONS = ["Administrator / Principal", "Office / Dismissal Staff", "Teacher", "District / IT", "Other"];

const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const maxISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 60);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const fmtTime = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${m.toString().padStart(2, "0")} ${ap}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
};

export default function BookingForm({ product = "campuspath", dark = false }) {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", school_name: "", location: "", role: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadSlots = (d) => {
    if (!d) { setSlots([]); return; }
    setSlotsLoading(true);
    api.get("/bookings/slots", { params: { date: d, product } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  };

  useEffect(() => {
    setTime("");
    loadSlots(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, product]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please choose a date and time slot.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", {
        product,
        date,
        time,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone || null,
        school_name: form.school_name,
        location: form.location || null,
        role: form.role || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Demo booked, confirmation on the way!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
      loadSlots(date);
      setTime("");
    } finally {
      setLoading(false);
    }
  };

  const heading = dark ? "text-white" : "text-ink";
  const muted = dark ? "text-white/60" : "text-ink/60";
  const faint = dark ? "text-white/40" : "text-ink/40";

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="rounded-2xl border border-leaf/40 bg-leaf/10 p-10 text-center"
        data-testid="booking-success"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-leaf text-ink">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <h3 className={`mt-6 font-display text-2xl font-800 ${heading}`}>You're booked, {form.first_name || "there"}.</h3>
        <p className={`mx-auto mt-3 max-w-sm ${muted}`}>
          Your demo is confirmed for <span className={`font-700 ${heading}`}>{fmtDate(date)} at {fmtTime(time)} ET</span>.
          A confirmation was sent to {form.email}.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <CTA to="/" variant={dark ? "leafDark" : "ink"} dataTestId="booking-home-btn">Back to home</CTA>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-7" data-testid="booking-form">
      <div>
        <Label htmlFor="booking-date" dark={dark} required>Pick a date</Label>
        <Input
          id="booking-date"
          type="date"
          dark={dark}
          dataTestId="booking-date"
          value={date}
          min={todayISO()}
          max={maxISO()}
          onChange={(e) => setDate(e.target.value)}
          required
        />
        <p className={`mt-2 text-xs ${faint}`}>Available weekdays, 9:00 AM – 5:00 PM Eastern Time (ET).</p>
      </div>

      {date && (
        <div data-testid="slot-picker">
          <Label dark={dark}>Choose a time (ET)</Label>
          {slotsLoading ? (
            <div className={`mt-3 text-sm ${faint}`} data-testid="slots-loading">Loading available times…</div>
          ) : slots.length === 0 ? (
            <div className={`mt-3 rounded-xl border px-4 py-4 text-sm ${dark ? "border-white/10 bg-white/5 text-white/50" : "border-ink/10 bg-bone text-ink/50"}`} data-testid="slots-empty">
              No open times on this date. Please choose another weekday.
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {slots.map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setTime(t)}
                  data-testid={`slot-${t}`}
                  className={`rounded-xl border px-2 py-2.5 text-sm font-600 transition-colors duration-200 ${
                    time === t
                      ? "border-leaf bg-leaf text-ink"
                      : dark
                        ? "border-white/15 text-white/70 hover:border-white/45"
                        : "border-ink/15 text-ink/70 hover:border-ink/40"
                  }`}
                >
                  {fmtTime(t)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label htmlFor="bf_first" dark={dark} required>First name</Label>
          <Input id="bf_first" dark={dark} dataTestId="booking-first-name" value={form.first_name} onChange={set("first_name")} required placeholder="Jane" />
        </div>
        <div>
          <Label htmlFor="bf_last" dark={dark} required>Last name</Label>
          <Input id="bf_last" dark={dark} dataTestId="booking-last-name" value={form.last_name} onChange={set("last_name")} required placeholder="Doe" />
        </div>
      </div>

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label htmlFor="bf_email" dark={dark} required>Work / school email</Label>
          <Input id="bf_email" type="email" dark={dark} dataTestId="booking-email" value={form.email} onChange={set("email")} required placeholder="jane@school.org" />
        </div>
        <div>
          <Label htmlFor="bf_phone" dark={dark}>Phone</Label>
          <Input id="bf_phone" dark={dark} dataTestId="booking-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
        </div>
      </div>

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <Label htmlFor="bf_school" dark={dark} required>School / district</Label>
          <Input id="bf_school" dark={dark} dataTestId="booking-school" value={form.school_name} onChange={set("school_name")} required placeholder="Lincoln Elementary" />
        </div>
        <div>
          <Label htmlFor="bf_role" dark={dark}>Your role</Label>
          <Select id="bf_role" dark={dark} dataTestId="booking-role" value={form.role} onChange={set("role")}>
            <option value="">Select a role…</option>
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="bf_message" dark={dark}>Anything we should prepare?</Label>
        <Textarea id="bf_message" dark={dark} dataTestId="booking-message" value={form.message} onChange={set("message")} placeholder="Tell us about your school…" />
      </div>

      <div className="flex items-center gap-4 pt-2">
        <button
          type="submit"
          disabled={loading}
          data-testid="booking-submit-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-leaf px-8 py-4 text-sm font-700 text-ink transition-[background-color,transform] duration-300 hover:bg-ink hover:text-white active:scale-[0.97] disabled:opacity-60"
        >
          {loading ? "Booking…" : "Confirm my demo"}
          {!loading && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="transition-transform duration-300 group-hover:translate-x-1"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
        </button>
        {time && date && <span className={`text-xs ${dark ? "text-white/50" : "text-ink/50"}`}>{fmtDate(date)} · {fmtTime(time)} ET</span>}
      </div>
    </form>
  );
}
